import { Formik, FormikHelpers } from 'formik';
import { StepContainer, StepContent, StepDescription, StepForm, StepHeading, StepNavigator, StepNextButton } from '../../../components';
import { AboutYouForm } from './form';
import { AddressVerificationProvider, useWithAddressVerify } from '@uplift/ui-v2';
import { ContactName, RewriteUserContactsErrorResponse, patchUser, patchUserProfile, rewriteUserContacts, updateMatchPreferences } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { getFormErrorsFromRewriteUserContactsErrorResponse, getRewriteUserContactsInputFromFormValues } from '@uplift/utils';
import { AxiosError } from 'axios';
import { useUserContactsQuery, useUserWithProfileQuery } from '@uplift/queries';
import { AboutYouFormType, aboutYouValidation } from './validation';
import { useL10n } from '@uplift/hooks';
import { backendBirthDateFormatToFormFormat, formBirthDateFormatToBackendFormat } from '../../../utils';
import { useQueryClient } from '@tanstack/react-query';

export type AboutYouProps = {
  onNext: () => void;
  onBack: () => void;
  banner: React.ReactNode;
  stateId: string;
  matchId: string;
};

type AboutYouContentProps = AboutYouProps & {
  initialValues: AboutYouFormType;
};

const AboutYouContent: React.VFC<AboutYouContentProps> = props => {
  const l10n = useL10n();
  const currentUser = useCurrentuser();
  const withAddressVerify = useWithAddressVerify();
  const queryClient = useQueryClient();

  const onSubmit = withAddressVerify(async (values: AboutYouFormType, formikHelpers: FormikHelpers<AboutYouFormType>) => {
    try {
      await rewriteUserContacts(currentUser.id,
        getRewriteUserContactsInputFromFormValues({
          [ContactName.mobile]: values.mobilePhone,
          [ContactName.home]: values.homePhone,
        })
      );
    } catch (error) {
      const errorObj = getFormErrorsFromRewriteUserContactsErrorResponse(error as AxiosError<RewriteUserContactsErrorResponse>);

      if (errorObj) {
        formikHelpers.setErrors({
          mobilePhone: errorObj[ContactName.mobile],
          homePhone: errorObj[ContactName.home]
        });

        return;
      }

      console.error('Error updating contacts', error);
      return;
    }

    try {
      await patchUser(currentUser.id, {
        firstName: values.firstName,
        middleName: values.middleName,
        lastName: values.lastName,
      });
    } catch (error) {
      console.error('Error updating user', error);
      return;
    }

    try {
      await patchUserProfile(currentUser.id, {
        preferredName: values.preferredName,
        dateOfBirth: formBirthDateFormatToBackendFormat(values.dateOfBirth),
        pronouns: values.pronouns,
        sex: values.sex,
        address: {
          line1: values.address.address1,
          line2: values.address.address2,
          city: values.address.city,
          stateId: values.address.state,
          zip: values.address.zip,
        },
      });
    } catch (error) {
      console.error('Error updating profile', error);
      return;
    }

    try {
      await updateMatchPreferences(props.matchId, {
        stateId: props.stateId,
      });
    } catch (error) {
      console.error('Error updating match preferences', error);
      return;
    }

    await queryClient.invalidateQueries([ 'user', currentUser.id ]);

    props.onNext();
  }, {
    shouldCheck: true,
    initialAddress: props.initialValues.address,
    getAddressFromFormikValues: (values: AboutYouFormType) => values.address,
  });

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={props.initialValues}
      validationSchema={aboutYouValidation(l10n)}
      validateOnMount
    >
      {formik => (
        <StepContainer>
          {props.banner}
          <StepContent>
            <StepHeading>About You</StepHeading>
            <StepDescription>Before we can schedule your session, we need to know a little more.</StepDescription>
            <StepForm onSubmit={formik.handleSubmit}>
              <AboutYouForm />
            </StepForm>
          </StepContent>
          <StepNavigator onBack={props.onBack}>
            <StepNextButton disabled={!formik.isValid} />
          </StepNavigator>
        </StepContainer>
      )}
    </Formik>
  );
};

export const AboutYou: React.VFC<AboutYouProps> = props => {
  const currentUser = useCurrentuser();
  const { data: contacts, isLoading: contactsLoading } = useUserContactsQuery(currentUser.id);
  const { data: userWithProfile, isLoading: userWithProfileLoading } = useUserWithProfileQuery(currentUser.id);
  const isLoading = contactsLoading || userWithProfileLoading;

  if (isLoading) {
    return null;
  }

  const mobilePhone = contacts?.find(c => c.name === ContactName.mobile);
  const homePhone = contacts?.find(c => c.name === ContactName.home);

  const initialValues: AboutYouFormType = {
    firstName: userWithProfile?.user?.firstName || '',
    preferredName: userWithProfile?.profile?.preferredName || '',
    middleName: userWithProfile?.user?.middleName || '',
    lastName: userWithProfile?.user?.lastName || '',
    sex: userWithProfile?.profile?.sex || null,
    pronouns: userWithProfile?.profile?.pronouns || '',
    dateOfBirth: userWithProfile?.profile?.dob ? backendBirthDateFormatToFormFormat(userWithProfile.profile.dob) : '',
    address: {
      address1: userWithProfile?.profile?.address?.line1 || '',
      address2: userWithProfile?.profile?.address?.line2 || '',
      city: userWithProfile?.profile?.address?.city || '',
      state: userWithProfile?.profile?.address?.stateId || '',
      zip: userWithProfile?.profile?.address?.zip || '',
    },
    mobilePhone: mobilePhone?.value || '',
    homePhone: homePhone?.value || '',
  };

  return (
    <AddressVerificationProvider
      shouldReverifyAddress={false}
      parsedVerifiedAddress={initialValues.address.address1 ? initialValues.address : null}
    >
      <AboutYouContent {...props} initialValues={initialValues} />
    </AddressVerificationProvider>
  );
};
